import * as api from '../core/api/api';
import HttpMethods from '../enums/http-methods';
import * as cmxDateTime from '../core/date-time/cmx-date-time';

import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { ImprovementAction } from './action-types';
import { fetchDictionaryRows } from './dictionary-utilities';
import { DocumentQueryType, DocumentStatus } from '../enums';
import { fetchActivity, updateProcessInstance } from './code-bench.actions';
import { ImprovementDictionaryProps } from '../enums';
import { sortByDate } from '../core/date-time/cmx-date-time';

const improvementSearch = createAction<CMxCommonApp.CodeQuery>(
  ImprovementAction.SEARCH
);

const getImprovementsForProcessInstance = createAsyncThunk<
  AmplifyAPI.DocumentImprovement[],
  AmplifyAPI.ProcessInstance,
  { rejectValue: any }
>(ImprovementAction.GET, async (processInstance, _thunkAPI) => {
  const path = `/coding-api/processinstance/${processInstance.serviceLine}/${processInstance.guid.uuid}/documentimprovement/v1`;
  const response = await api.requestJSON<AmplifyAPI.DocumentImprovement[]>(
    path,
    {
      method: HttpMethods.GET
    }
  );
  const improvements = response
    .sort((a, b) => sortByDate(a.created_date, b.created_date))
    .reverse();
  return improvements;
});

const getImprovementChanges = async (
  dispatch: any,
  processInstance: AmplifyAPI.ProcessInstance
): Promise<AmplifyAPI.DocumentImprovement[]> => {
  const action = await dispatch(getImprovementsForProcessInstance(processInstance));
  dispatch(fetchActivity(processInstance) as any);
  dispatch(
    updateProcessInstance({
      serviceLine: processInstance.serviceLine,
      guid: processInstance.guid.uuid
    }) as any
  );
  return action.payload;
};

const addImprovement = createAsyncThunk<
  AmplifyAPI.DocumentImprovement[],
  AmplifyAPI.ImprovementRequest,
  { rejectValue: any }
>(
  ImprovementAction.ADD,
  async ({ improvementDictionaryRow, processInstance }, { dispatch }) => {
    const path = `/coding-api/processinstance/${processInstance.serviceLine}/${processInstance.guid.uuid}/documentimprovement/v1`;
    const requiresProvider = new Set([
      DocumentQueryType.REQUIRED,
      DocumentQueryType.CODERS_CHOICE
    ]);
    await api.requestJSON<AmplifyAPI.DocumentImprovement[]>(
      path,
      {
        method: HttpMethods.POST,
        body: JSON.stringify([
          {
            code:
              improvementDictionaryRow[ImprovementDictionaryProps.ERROR_CODE],
            description:
              improvementDictionaryRow[ImprovementDictionaryProps.DESCRIPTION],
            provider_query: requiresProvider.has(
              improvementDictionaryRow[
                ImprovementDictionaryProps.SEND_QUERY
              ] as DocumentQueryType
            ),
            document_status: DocumentStatus.OPEN
          }
        ])
      }
    );
    return getImprovementChanges(dispatch, processInstance);
  }
);

const updateImprovement = createAsyncThunk<
  AmplifyAPI.DocumentImprovement[],
  AmplifyAPI.ImprovementRequest,
  { rejectValue: any }
>(
  ImprovementAction.UPDATE,
  async ({ documentImprovement, processInstance }, { dispatch }) => {
    const path = `/coding-api/processinstance/${processInstance.serviceLine}/${processInstance.guid.uuid}/documentimprovement/v1`;
    await api.requestJSON<AmplifyAPI.DocumentImprovement[]>(
      path,
      {
        method: HttpMethods.PATCH,
        body: JSON.stringify([documentImprovement])
      }
    );
    return getImprovementChanges(dispatch, processInstance);
  }
);

const removeImprovement = createAsyncThunk<
  AmplifyAPI.DocumentImprovement[],
  AmplifyAPI.ImprovementRequest,
  { rejectValue: any }
>(
  ImprovementAction.REMOVE,
  async ({ documentImprovement, processInstance }, { dispatch }) => {
    const path = `/coding-api/processinstance/${processInstance.serviceLine}/${processInstance.guid.uuid}/documentimprovement/v1?documentImprovementGuid=${documentImprovement?.document_improvement_guid}`;
    await api.request(path, {
      method: HttpMethods.DELETE
    });
    return getImprovementChanges(dispatch, processInstance);
  }
);

const fetchImprovementOptions = createAsyncThunk<
  Accelerate.DictionaryRowData<Accelerate.ImprovementDictionaryRow>,
  AmplifyAPI.ImprovementRequest,
  { rejectValue: any }
>(
  ImprovementAction.FETCH_IMPROVEMENT_OPTIONS,
  async (improvementRequest, _thunkAPI) => {
    const currentDate = cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE);
    const searchParams = new URLSearchParams({
      tenantId: improvementRequest.processInstance.healthSystemTenantId.uuid,
      derivedIndicator: 'true',
      effectiveDate: currentDate,
      dictionaryName: 'Documentation Opportunity',
      columnName: 'Key'
    });

    return fetchDictionaryRows<Accelerate.ImprovementDictionaryRow>(
      searchParams
    );
  }
);

const addImprovementProvider = createAsyncThunk<
  AmplifyAPI.DocumentImprovement[],
  AmplifyAPI.ImprovementRequest,
  { rejectValue: any }
>(
  ImprovementAction.ADD_PROVIDER,
  async (
    { documentImprovement, processInstance, improvementProvider },
    { dispatch }
  ) => {
    const path = `/coding-api/processinstance/${processInstance.serviceLine}/${processInstance.guid.uuid}/documentimprovement/${documentImprovement.document_improvement_guid}/provider/v1`;
    await api.requestJSON<AmplifyAPI.ImprovememtProvider[]>(
      path,
      {
        method: HttpMethods.POST,
        body: JSON.stringify([improvementProvider])
      }
    );
    return getImprovementChanges(dispatch, processInstance);
  }
);

const removeImprovementProvider = createAsyncThunk<
  AmplifyAPI.DocumentImprovement[],
  AmplifyAPI.ImprovementRequest,
  { rejectValue: any }
>(
  ImprovementAction.REMOVE_PROVIDER,
  async (
    { documentImprovement, processInstance, improvementProvider },
    { dispatch }
  ) => {
    const path = `/coding-api/processinstance/${processInstance.serviceLine}/${processInstance.guid.uuid}/documentimprovement/${documentImprovement.document_improvement_guid}/provider/v1`;
    await api.request(path, {
      method: HttpMethods.DELETE,
      body: JSON.stringify([improvementProvider])
    });
    return getImprovementChanges(dispatch, processInstance);
  }
);

const updateImprovementProvider = createAsyncThunk<
  AmplifyAPI.DocumentImprovement[],
  AmplifyAPI.ImprovementRequest,
  { rejectValue: any }
>(
  ImprovementAction.UPDATE_PROVIDER,
  async (
    { documentImprovement, processInstance, improvementProvider },
    { dispatch }
  ) => {
    const path = `/coding-api/processinstance/${processInstance.serviceLine}/${processInstance.guid.uuid}/documentimprovement/${documentImprovement.document_improvement_guid}/provider/v1`;
    await api.requestJSON<AmplifyAPI.DocumentImprovement[]>(
      path,
      {
        method: HttpMethods.PUT,
        body: JSON.stringify([improvementProvider])
      }
    );
    return getImprovementChanges(dispatch, processInstance);
  }
);

export {
  addImprovement,
  updateImprovement,
  removeImprovement,
  improvementSearch,
  getImprovementsForProcessInstance,
  fetchImprovementOptions,
  addImprovementProvider,
  removeImprovementProvider,
  updateImprovementProvider
};
